import { Flex, Space } from "antd";
import { ProjectTag } from "../../types";
import { ProjectPill } from "./ProjectPill";
import { useState } from "react";
import { TagIcon, XIcon } from "@phosphor-icons/react";
import { useTranslate } from "@refinedev/core";
import { isReservedProjectTag } from "../helpers/project-create-wizard";
import { useAntTheme } from "hooks/useAntTheme";
import { useProjectTags } from "hooks";
import { EditableTagInput } from "components/EditableTagInput";
import { DashedButton } from "@libertify/ui";
import { CancelLinkBtn } from "./drawer/drawer-styled";

export const ProjectTagList = ({
  projectId,
  tags,
}: {
  projectId?: string;
  tags?: ProjectTag[];
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const t = useTranslate();
  const { theme } = useAntTheme();

  const {
    tagValues,
    editInputIndex,
    editInputRef,
    addInputRef,
    isUpdating,
    isSaving,
    handlers,
    setEditInputIndex,
  } = useProjectTags(projectId, tags);

  const { onAddNewTag, onEditConfirm, onTagDoubleClick, onTagClose, onReset } =
    handlers;

  const handleCancelEdit = () => {
    onReset();
    setIsEditing(false);
  };

  return (
    <Flex wrap gap={10} style={{ width: "100%" }} align="center">
      <Space wrap>
        {tagValues.map((tag, index) => {
          const isEditingCustomTag =
            isEditing && !isReservedProjectTag(tag.tag_key);
          if (editInputIndex === index) {
            return (
              <EditableTagInput
                defaultValue={tag.tag_value}
                key={tag.tag_key}
                onConfirm={onEditConfirm}
                onBlur={() => {
                  setEditInputIndex(-1);
                  if (editInputRef.current?.input) {
                    editInputRef.current.input.value = "";
                  }
                }}
                ref={editInputRef}
                theme={theme}
                loading={isSaving}
                teal
              />
            );
          }
          const isCustomTag = !isReservedProjectTag(tag.tag_key);
          return (
            <ProjectPill
              key={tag.tag_key}
              closeIcon={<XIcon size={12} color="#0d9488" weight="bold" />}
              closable={isCustomTag}
              onClose={() => onTagClose(index)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              <span
                role={isEditingCustomTag ? "button" : undefined}
                onMouseDown={(e) => {
                  if (!isEditingCustomTag) return;
                  // Prevent blur on add input when clicking to edit
                  e.preventDefault();
                }}
                onDoubleClick={(e) => {
                  if (!isEditingCustomTag) return;
                  e.preventDefault();
                  onTagDoubleClick(tag, index);
                }}
                onKeyDown={(e) => {
                  if (!isEditingCustomTag) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onTagDoubleClick(tag, index);
                  }
                }}
              >
                {tag.tag_value}
              </span>
            </ProjectPill>
          );
        })}
        {isEditing && (
          <EditableTagInput
            onConfirm={onAddNewTag}
            ref={addInputRef}
            theme={theme}
            teal
            prefix={<TagIcon weight="thin" size={18} />}
            placeholder={t(
              "media.components.ProjectVideo.ProjectTagList.addTag"
            )}
            styles={{
              prefix: {
                transform: "scaleX(-1)",
                color: "#0d9488",
              },
            }}
            loading={isSaving}
          />
        )}
      </Space>
      {isEditing && (
        <CancelLinkBtn
          onClick={handleCancelEdit}
          disabled={isUpdating}
          onMouseDown={(e) => {
            // Prevent blur on add input when clicking cancel
            e.preventDefault();
          }}
        >
          {t("buttons.cancel")}
        </CancelLinkBtn>
      )}

      {!isEditing && (
        <DashedButton onClick={() => setIsEditing(true)}>
          +{" "}
          {t("media.components.ProjectVideo.ProjectTagList.addTag", "Add tag")}
        </DashedButton>
      )}
    </Flex>
  );
};
